/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */

import React, { useEffect, useState } from 'react';
import { useFetch, PageTitle, FormAlerts, Button, Field } from '@cordata/buffet';
import {
  Card,
  CardBody,
  CardFooter,
  Row,
  Col,
  Alert,
  CardTitle,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { withFormik, FieldArray } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Yup from 'yup';
import { withRouter, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import kebabcaseKeys from 'kebabcase-keys';
import { find } from 'lodash';
import { all } from 'axios';

import {
  authorizedPyInstance as axios,
  authorizedInstanceNoBase as axiosFullUrl,
} from '~/utils';

import { PageLayout, Navbar } from '~/components';

const method = id => (id ? 'put' : 'post');
const url = (URL, id) => `${URL}${id ? `/${id}` : ''}`;

const saveDistributionList = (f, dl) =>
  all(dl.map(e =>
    axios[method(e.id)](url('api/data/public/facility-distribution-list', e.id), kebabcaseKeys({
      ...e,
      facilityUid: f.attributes.facilityUid,
    }, { deep: true }))));

const formikHoc = withFormik({
  displayName: 'HandleWithCareDetails',
  validateOnBlur: false,
  validateOnChange: false,

  mapPropsToValues: ({ location: { state } }) => ({
    ...state,
    childFirstName: state.name.split(', ')[1],
    childLastName: state.name.split(', ')[0],
    childGender: state.genderLabel,
    childAge: parseInt(state.age, 10),
    facility: state.facilityName,
    hwcPatient: {},
  }),

  validationSchema: Yup.object().shape({
    facility: Yup.mixed().required(),
    facilityDistributionLists: Yup.array().of(Yup.object().shape({
      emailAddress: Yup.string()
        .email().required(),
      name: Yup.string().required(),
    })).required(),
  }),

  handleSubmit: (values, {
    setStatus, setErrors, setSubmitting, props: { history },
  }) => {
    if (values.facility.isNew) {
      let facilityUid;
      axios
        .post(
          'api/data/public/facility',
          kebabcaseKeys({
            ...values.facility.attributes,
            orgUid: JSON.parse(localStorage.getItem('current')).data.relationships.currentDivision.relationships.organization.data.id,
            isInSelections: true,
          }, { deep: true }),
        )
        .then(r => r.data.data)
        .then((r) => {
          facilityUid = r.id;

          saveDistributionList(r, values.facilityDistributionLists);
        })
        .then(() =>
          axios
            .post(
              'api/data/public/organization-facility',
              kebabcaseKeys({
                facilityUid,
                orgUid: JSON.parse(localStorage.getItem('current')).data.relationships.currentDivision.relationships.organization.data.id,
              }, { deep: true }),
            ))
        .then(() => axios
          .put(
            `api/data/public/hwc-patient/${values.hwcPatientUid}`,
            kebabcaseKeys({
              ...values.hwcPatient,
              facilityUid,
            }, { deep: true }),
          ))
        .then(() =>
          axios
            .put(`api/data/public/hwc-patient/${values.hwcPatientUid}/command/resend`))
        .then(() => {
          setStatus({ success: true });

          setTimeout(() => history.push('/home'), 2500);
        })
        .catch(e => setErrors({ server: e }))
        .then(() => setSubmitting(false));
    } else {
      saveDistributionList(values.facility, values.facilityDistributionLists)
        .then(() => axios
          .put(
            `api/data/public/hwc-patient/${values.hwcPatientUid}`,
            kebabcaseKeys({
              ...values.hwcPatient,
              facilityUid: values.facility.attributes.facilityUid,
              facilityName: values.facility.attributes.facilityName,
            }, { deep: true }),
          ))
        .then(() =>
          axios
            .put(`api/data/public/hwc-patient/${values.hwcPatientUid}/command/resend`))
        .then(() => {
          setStatus({ success: true });

          setTimeout(() => history.push('/home'), 2500);
        })
        .catch(e => setErrors({ server: e }))
        .then(() => setSubmitting(false));
    }
  },
});

const HwcDetails = ({
  location: { state },
  values,
  errors,
  status,
  handleSubmit,
  submitCount,
  isSubmitting,
  setFieldValue,
  history,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [facilityStatus, facilities] = useFetch('data/public/facility?page-size=1000&page=1&sort=default-sort-order,facility-name&is-in-selections=true', { instance: 'p' });
  const [hwcPatientStatus, hwcPatient] = useFetch(`data/public/hwc-patient/${state.hwcPatientUid}`, { instance: 'p' });

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const ignore = () =>
    axios
      .put(
        `api/data/public/hwc-patient/${state.hwcPatientUid}`,
        kebabcaseKeys({
          ...values.hwcPatient,
          isIgnored: true,
        }, { deep: true }),
      )
      .then(() => history.push('/home'));

  const fetchDistList = f => axiosFullUrl
    .get(`/api/data/public/facility-distribution-list?facility-uid=${f.attributes.facilityUid}`)
    .then(r => r.data.data)
    .then(r => setFieldValue('facilityDistributionLists', r.map(rr => ({
      ...rr,
      name: rr.attributes.name,
      emailAddress: rr.attributes.emailAddress,
    }))))
    .catch(console.error);

  useEffect(() => {
    if (hwcPatientStatus === 'success') {
      setFieldValue('hwcPatient', hwcPatient.attributes);
    }
  }, [hwcPatientStatus]);

  useEffect(() => {
    if (
      facilities &&
      facilities.length &&
      typeof values.facility === 'string'
    ) {
      setFieldValue('facility', find(facilities, f => f.attributes.facilityName === values.facility) || {
        id: values.facility,
        isNew: true,
        attributes: {
          facilityName: values.facility,
        },
      });
    }
  }, [facilities, values.facility]);

  useEffect(() => {
    if (values.facility && typeof values.facility !== 'string') {
      setFieldValue('facilityDistributionLists', undefined);

      if (values.facility.relationships) {
        fetchDistList(values.facility);
      }
    }
  }, [values.facility]);

  return (
    <PageLayout Header={Navbar}>
      <PageTitle
        action={
          <Button
            color="danger"
            type="button"
            outline
            disabled={isSubmitting}
            onClick={toggleModal}
          >
            <FontAwesomeIcon icon="ban" />
            <span className="d-none d-md-inline ml-2">Dismiss</span>
          </Button>
        }
      >
        Handle with Care
      </PageTitle>

      <Card>
        <form onSubmit={handleSubmit} noValidate>
          <CardBody>
            {submitCount >= 1 && errors && (
              <FormAlerts errors={errors} />
            )}

            {status && status.success && (<Alert color="success">Form Sent!</Alert>)}

            <Field
              name="facility"
              label="School"
              type="select"
              loading={facilityStatus === 'loading'}
              options={facilities}
              onChange={(e, f) => {
                setFieldValue(e, f);

                if (f.relationships) {
                  fetchDistList(f);
                }
              }}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes && o.attributes.facilityName}
              creatable
              getNewOptionData={(inputValue, optionLabel) => ({
                isNew: true,
                id: optionLabel,
                attributes: { facilityName: optionLabel },
              })}
              required
            />

            <CardTitle>Facility Distribution Lists</CardTitle>

            {submitCount >= 1 && errors.facilityDistributionLists && (<Alert color="danger">Need at least one email!</Alert>)}

            <FieldArray
              name="facilityDistributionLists"
              render={({ push, remove }) => (
                <>
                  {values.facilityDistributionLists &&
                  values.facilityDistributionLists.map((e, i) => (
                    <React.Fragment key={i}>
                      {!values.facilityDistributionLists[i].id && (
                        <Button
                          size="sm"
                          outline
                          color="danger"
                          spear
                          type="button"
                          onClick={() => (e.id ? axios.delete(`api/public/data/facility-distribution-list/${e.id}`).then(() => remove(i)) : remove(i))}
                          disabled={!!values.facilityDistributionLists[i].id}
                        >
                          <FontAwesomeIcon icon="minus" />
                          <span className="d-none d-md-inline ml-2">Remove Email Address</span>
                        </Button>
                      )}

                      <Row form>
                        <Col sm>
                          <Field
                            name={`facilityDistributionLists.${i}.name`}
                            label="Name"
                            placeholder="Enter Name"
                            disabled={!!values.facilityDistributionLists[i].id}
                            required
                          />
                        </Col>

                        <Col sm>
                          <Field
                            name={`facilityDistributionLists.${i}.emailAddress`}
                            label="Email Address"
                            placeholder="Enter Email Address"
                            type="email"
                            disabled={!!values.facilityDistributionLists[i].id}
                            required
                          />
                        </Col>
                      </Row>

                      {i !== values.facilityDistributionLists.length - 1 && <hr />}
                    </React.Fragment>
                  ))}

                  <Button
                    size="sm"
                    outline
                    color="tertiary"
                    spear
                    type="button"
                    onClick={() => push({ name: '', emailAddress: '' })}
                  >
                    <FontAwesomeIcon icon="plus" />
                    <span className="d-none d-md-inline ml-2">Add Email Address</span>
                  </Button>
                </>
              )}
            />

            <hr />

            <Field
              name="hwcPatient.didntSendReason"
              label="Didn't Send Reason"
              disabled
            />

            <Row form>
              <Col sm>
                <Field
                  name="officerName"
                  disabled
                />
              </Col>

              <Col sm>
                <Field
                  name="createdDate"
                  type="date"
                  disabled
                />
              </Col>
            </Row>

            <Row form>
              <Col md>
                <Field
                  name="childFirstName"
                  disabled
                />
              </Col>

              <Col md>
                <Field
                  name="childLastName"
                  disabled
                />
              </Col>

              <Col md>
                <Field
                  name="childGender"
                  disabled
                />
              </Col>
            </Row>

            <Row form>
              <Col md={5}>
                <Field
                  name="childBirthDate"
                  type="date"
                  disabled
                />
              </Col>

              <Col md={2} className="text-center text-muted mt-md-5">
                - or -
              </Col>

              <Col md={5}>
                <Field
                  name="childAge"
                  type="number"
                  disabled
                />
              </Col>
            </Row>
          </CardBody>

          <CardFooter className="d-flex">
            <div className="ml-auto">
              <Button
                type="cancel"
                disabled={isSubmitting}
                tag={Link}
                to="/home"
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Working ...' : 'Save'}
              </Button>
            </div>
          </CardFooter>
        </form>
      </Card>

      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Dismissal Confirmation</ModalHeader>

        <ModalBody>
          <em>This action cannot be undone.</em> Please confirm you want to dismiss this entry.
        </ModalBody>

        <ModalFooter>
          <Button type="cancel" color="tertiary" outline onClick={toggleModal}>Cancel</Button>

          <Button color="danger" onClick={ignore}>
            <FontAwesomeIcon icon="ban" />
            <span className="d-none d-md-inline ml-2">Confirm Dismissal</span>
          </Button>
        </ModalFooter>
      </Modal>
    </PageLayout>
  );
};

HwcDetails.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape().isRequired,
  }).isRequired,
};

export default withRouter(formikHoc(HwcDetails));
